import React, { useState, useEffect } from "react";
import { Box, Typography, Chip } from "@mui/material";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { Meeting } from "../types";
import { MeetingStatusKey, meetingStatusColors } from "../utils/statusColors";
import { fromServerToLocal } from "../utils/time";
import StatusChangeModal from "./Modals/StatusChangeModal";
import { updateMeeting } from "../services/meetingService";
import { getCachedStatuses } from "../storage/preloadData";

interface MeetingViewProps {
  meeting: Meeting;
  onStatusChange?: () => void;
  onStatusUpdateSuccess?: (message: string) => void;
}

const MeetingView: React.FC<MeetingViewProps> = ({
  meeting,
  onStatusChange,
  onStatusUpdateSuccess,
}) => {
  const { t, i18n } = useTranslation();
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [availableStatuses, setAvailableStatuses] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const loadStatuses = async () => {
      try {
        const statuses = await getCachedStatuses();
        const meetingStatuses = statuses.meetings || [];
        if (meetingStatuses.length > 0) {
          setAvailableStatuses(meetingStatuses);
        } else {
          setAvailableStatuses(["Planned", "Held", "Not Held"]);
        }
      } catch (err) {
        console.error("Failed to load meeting statuses:", err);
        setAvailableStatuses(["Planned", "Held", "Not Held"]);
      }
    };
    loadStatuses();
  }, []);

  const handleStatusSave = async (newStatus: string) => {
    setIsLoading(true);
    try {
      await updateMeeting(meeting.id, { status: newStatus });
      if (onStatusChange) {
        await onStatusChange();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const statusColorsMap: Record<string, string> = Object.keys(
    meetingStatusColors,
  ).reduce(
    (acc, key) => {
      acc[key] = meetingStatusColors[key as MeetingStatusKey].color;
      return acc;
    },
    {} as Record<string, string>,
  );

  const dateFormat =
    i18n.language === "en_US" ? "DD.MM.YYYY HH:mm" : "DD.MM.YYYY HH:mm";

  const startLocal = meeting.dateStart
    ? fromServerToLocal(meeting.dateStart)
    : null;
  const endLocal = meeting.dateEnd ? fromServerToLocal(meeting.dateEnd) : null;

  const formattedStart =
    startLocal && dayjs(startLocal).isValid()
      ? dayjs(startLocal).format(dateFormat)
      : null;

  const formattedEnd =
    endLocal && dayjs(endLocal).isValid()
      ? dayjs(endLocal).format(dateFormat)
      : null;

  const normalizedStatus =
    meeting.status?.toLowerCase().replace(/\s/g, "_") || "";
  const statusText = t(`statuses.meeting.${normalizedStatus}`, meeting.status);
  const statusColor =
    meetingStatusColors[meeting.status as MeetingStatusKey]?.color || "#757575";

  let durationText = null;
  if (formattedStart && formattedEnd) {
    const start = dayjs(startLocal);
    const end = dayjs(endLocal);
    const hours = end.diff(start, "hour", true);
    const minutes = end.diff(start, "minute") % 60;
    const hoursKey = t("meetingDetail.hours");
    const minutesKey = t("meetingDetail.minutes");
    if (hours >= 1) {
      if (minutes === 0) {
        durationText = `${Math.floor(hours)} ${hoursKey}`;
      } else {
        durationText = `${Math.floor(hours)} ${hoursKey} ${minutes} ${minutesKey}`;
      }
    } else {
      durationText = `${minutes} ${minutesKey}`;
    }
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Box display="flex" flexDirection="column" gap={2.5}>
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontSize: "0.75rem",
              fontWeight: 500,
              mb: 0.5,
              display: "block",
            }}
          >
            {t("meetingDetail.nameLabel") || "Название"}
          </Typography>
          <Typography
            variant="body1"
            sx={{ fontSize: "1rem", fontWeight: 500 }}
          >
            {meeting.name}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: { xs: 3, sm: 4 },
            alignItems: "flex-start",
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: "0.75rem",
                fontWeight: 500,
                mb: 0.5,
                display: "block",
              }}
            >
              {t("meetingDetail.statusLabel") || "Статус"}
            </Typography>
            <Chip
              label={statusText}
              onClick={() => setStatusModalOpen(true)}
              sx={{
                backgroundColor: statusColor,
                color: "#fff",
                borderRadius: "16px",
                py: 0.5,
                fontSize: "0.875rem",
                fontWeight: 600,
                height: "auto",
                cursor: "pointer",
                "&:hover": {
                  opacity: 0.8,
                },
              }}
            />
          </Box>

          {meeting.assignedUserName && (
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  mb: 0.5,
                  display: "block",
                }}
              >
                {t("meetingDetail.assigneeLabel") || "Ответственный"}
              </Typography>
              <Typography variant="body1" sx={{ fontSize: "1rem" }}>
                {meeting.assignedUserName}
              </Typography>
            </Box>
          )}
        </Box>

        {(formattedStart || formattedEnd) && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: { xs: 2, sm: 4 },
              alignItems: "flex-start",
            }}
          >
            {formattedStart && (
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    mb: 0.5,
                    display: "block",
                  }}
                >
                  {t("meetingDetail.startDateLabel") || "Дата начала"}
                </Typography>
                <Typography variant="body1" sx={{ fontSize: "1rem" }}>
                  {formattedStart}
                </Typography>
              </Box>
            )}
            {formattedEnd && (
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    mb: 0.5,
                    display: "block",
                  }}
                >
                  {t("meetingDetail.endDateLabel") || "Дата окончания"}
                </Typography>
                <Typography variant="body1" sx={{ fontSize: "1rem" }}>
                  {formattedEnd}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {durationText && (
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: "0.75rem",
                fontWeight: 500,
                mb: 0.5,
                display: "block",
              }}
            >
              {t("meetingDetail.durationLabel") || "Продолжительность"}
            </Typography>
            <Typography variant="body1" sx={{ fontSize: "1rem" }}>
              {durationText}
            </Typography>
          </Box>
        )}

        {meeting.group && (
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: "0.75rem",
                fontWeight: 500,
                mb: 0.5,
                display: "block",
              }}
            >
              {t("meetingDetail.groupLabel") || "Группа"}
            </Typography>
            <Typography variant="body1" sx={{ fontSize: "1rem" }}>
              {meeting.group}
            </Typography>
          </Box>
        )}

        {meeting.description && (
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: "0.75rem",
                fontWeight: 500,
                mb: 0.5,
                display: "block",
              }}
            >
              {t("meetingDetail.descriptionLabel") || "Описание"}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: "1rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {meeting.description}
            </Typography>
          </Box>
        )}
      </Box>
      <StatusChangeModal
        open={statusModalOpen}
        onClose={() => setStatusModalOpen(false)}
        onSave={handleStatusSave}
        currentStatus={meeting.status}
        availableStatuses={availableStatuses}
        statusType="meeting"
        statusColors={statusColorsMap}
        isLoading={isLoading}
      />
    </Box>
  );
};

export default MeetingView;
